import { useCallback, useEffect, useMemo } from 'react';

import type { Order } from '@src/domains/orders';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { buyerSelector } from '@src/features/customers';
import { loadOrders, ordersPageSelector } from '@src/features/orders/store/order';

import { isPositiveAmount } from '../../core/isPositiveAmount';
import {
  buildCashbackGrowthOrderSnapshot,
  findLatestOrder,
  findOrderMatchingSnapshot,
  isCashbackGrowthEligibleOrder,
  type CashbackGrowthOrderSnapshot,
} from '../../core/cashbackGrowthOrderSnapshot';
import { useCashbackGrowthDelayedModal } from '../../hooks/useCashbackGrowthDelayedModal';
import { useCashbackGrowthPersistence } from '../../hooks/useCashbackGrowthPersistence';

export type UseCashbackGrowthOrchestratorParams = {
  order?: Order;
};

export const useCashbackGrowthOrchestrator = (params: UseCashbackGrowthOrchestratorParams = {}) => {
  const { order: checkoutOrder } = params;
  const isCheckout = Boolean(checkoutOrder);

  const dispatch = useAppDispatch();
  const [buyer] = useAppSelector(buyerSelector);
  const { orders } = useAppSelector(ordersPageSelector(1));

  useEffect(() => {
    if (!isCheckout) {
      dispatch(loadOrders(1));
    }
  }, [dispatch, isCheckout]);

  const latestOrder = useMemo(() => (isCheckout ? null : findLatestOrder(orders)), [isCheckout, orders]);

  const fallbackOrderId = checkoutOrder?.id ?? latestOrder?.id;

  const {
    modalDismissed,
    showBanner,
    persistSnapshot,
    dismiss,
    snapshot: persistedSnapshot,
  } = useCashbackGrowthPersistence(fallbackOrderId, {
    validateSnapshotAgainstOrders: !isCheckout,
    // Only the latest purchase — never fall back to an older eligible order.
    orders: latestOrder ? [latestOrder] : [],
  });

  const checkoutSnapshot = useMemo((): CashbackGrowthOrderSnapshot | null => {
    if (!checkoutOrder) {
      return null;
    }

    return buildCashbackGrowthOrderSnapshot(checkoutOrder, buyer?.firstName ?? undefined);
  }, [buyer?.firstName, checkoutOrder]);

  const accountSnapshot = useMemo((): CashbackGrowthOrderSnapshot | null => {
    if (isCheckout || !latestOrder || !isCashbackGrowthEligibleOrder(latestOrder)) {
      return null;
    }

    const matchedOrder = findOrderMatchingSnapshot([latestOrder], persistedSnapshot);
    if (matchedOrder) {
      return buildCashbackGrowthOrderSnapshot(
        matchedOrder,
        persistedSnapshot?.buyerFirstName ?? buyer?.firstName ?? undefined,
      );
    }

    return buildCashbackGrowthOrderSnapshot(latestOrder, buyer?.firstName ?? undefined);
  }, [buyer?.firstName, isCheckout, latestOrder, persistedSnapshot]);

  const snapshot = isCheckout ? checkoutSnapshot : accountSnapshot;

  const eligible = isCheckout
    ? Boolean(checkoutOrder && isPositiveAmount(checkoutOrder.finalAmountCurrencyInfo.amountCents))
    : Boolean(accountSnapshot);

  useEffect(() => {
    if (isCheckout && eligible && checkoutSnapshot) {
      persistSnapshot(checkoutSnapshot);
    }
  }, [checkoutSnapshot, eligible, isCheckout, persistSnapshot]);

  const { modalOpen, closeModal } = useCashbackGrowthDelayedModal({
    active: eligible && !modalDismissed,
  });

  const handleDismiss = useCallback(() => {
    if (!snapshot) {
      return;
    }

    dismiss(snapshot);
    closeModal();
  }, [closeModal, dismiss, snapshot]);

  return {
    eligible,
    snapshot,
    modalOpen,
    showBanner,
    handleDismiss,
  };
};
